
// Production environment configuration
import { isDevelopment, isProduction } from './security';

interface EnvironmentConfig {
  supabase: {
    url: string;
    anonKey: string;
  };
  mercadoPago: {
    publicKey: string;
    accessToken?: string;
  };
  app: {
    domain: string;
    siteUrl: string;
    name: string;
    environment: 'development' | 'staging' | 'production';
  };
  security: {
    adminEmails: string[];
    sessionTimeout: number;
    maxLoginAttempts: number;
  };
  api: {
    timeout: number;
    retryAttempts: number;
  };
}

// Get environment from various sources
const getEnvironment = (): 'development' | 'staging' | 'production' => {
  // Check for explicit environment variable
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'psyonline.clinic') return 'production';
    if (hostname.includes('staging') || hostname.includes('preview')) return 'staging';
  }
  
  // Fallback to NODE_ENV or default to development
  return (process.env.NODE_ENV as 'development' | 'staging' | 'production') || 'development';
};

const environment = getEnvironment();

// Production configuration
const getProductionConfig = (): EnvironmentConfig => ({
  supabase: {
    url: "https://olpuhxpkqkwojdoricuo.supabase.co",
    anonKey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9scHVoeHBrcWt3b2pkb3JpY3VvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc3ODM1MjIsImV4cCI6MjA2MzM1OTUyMn0.70DnDLI7tgHjjPxpOttq4ZGD3-iFTzHR6NO8VeHZG10"
  },
  mercadoPago: {
    // Production keys - estas devem ser configuradas via secrets do Supabase
    publicKey: "APP_USR-YOUR_PRODUCTION_PUBLIC_KEY", // Substitua pela chave real de produção
    accessToken: "APP_USR-YOUR_PRODUCTION_ACCESS_TOKEN" // Configurado via Supabase secrets
  },
  app: {
    domain: "psyonline.clinic",
    siteUrl: "https://psyonline.clinic",
    name: "PsyOnline",
    environment: "production"
  },
  security: {
    adminEmails: ['admin@psyonline.clinic'], // Admin real para produção
    sessionTimeout: 30 * 60 * 1000, // 30 minutes
    maxLoginAttempts: 5,
  },
  api: {
    timeout: 10000, // 10 seconds
    retryAttempts: 3,
  }
});

// Staging configuration
const getStagingConfig = (): EnvironmentConfig => ({
  supabase: {
    url: "https://olpuhxpkqkwojdoricuo.supabase.co",
    anonKey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9scHVoeHBrcWt3b2pkb3JpY3VvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc3ODM1MjIsImV4cCI6MjA2MzM1OTUyMn0.70DnDLI7tgHjjPxpOttq4ZGD3-iFTzHR6NO8VeHZG10"
  },
  mercadoPago: {
    // Staging/Test keys
    publicKey: "TEST-ceb54ba7-3e7c-41c8-9b3c-b85970cf8d88",
    accessToken: "TEST-YOUR_STAGING_ACCESS_TOKEN"
  },
  app: {
    domain: "staging.psyonline.clinic",
    siteUrl: "https://staging.psyonline.clinic",
    name: "PsyOnline Staging",
    environment: "staging"
  },
  security: {
    adminEmails: ['admin@psyonline.clinic', 'staging@psyonline.clinic'],
    sessionTimeout: 60 * 60 * 1000, // 1 hour for staging
    maxLoginAttempts: 10,
  },
  api: {
    timeout: 15000, // 15 seconds for staging
    retryAttempts: 2,
  }
});

// Development configuration
const getDevelopmentConfig = (): EnvironmentConfig => ({
  supabase: {
    url: "https://olpuhxpkqkwojdoricuo.supabase.co",
    anonKey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9scHVoeHBrcWt3b2pkb3JpY3VvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDc3ODM1MjIsImV4cCI6MjA2MzM1OTUyMn0.70DnDLI7tgHjjPxpOttq4ZGD3-iFTzHR6NO8VeHZG10"
  },
  mercadoPago: {
    // Development/Test keys
    publicKey: "TEST-ceb54ba7-3e7c-41c8-9b3c-b85970cf8d88",
  },
  app: {
    domain: "localhost:8080",
    siteUrl: "http://localhost:8080",
    name: "PsyOnline Dev",
    environment: "development"
  },
  security: {
    adminEmails: ['admin@psyonline.clinic'], // Mesmo admin para desenvolvimento
    sessionTimeout: 24 * 60 * 60 * 1000, // 24 hours for development
    maxLoginAttempts: 50,
  },
  api: {
    timeout: 30000, // 30 seconds for development
    retryAttempts: 1,
  }
});

// Get configuration based on environment
export const getEnvironmentConfig = (): EnvironmentConfig => {
  switch (environment) {
    case 'production':
      return getProductionConfig();
    case 'staging':
      return getStagingConfig();
    case 'development':
    default:
      return getDevelopmentConfig();
  }
};

// Export current config
export const config = getEnvironmentConfig();

// Environment helpers
export const isProductionEnvironment = () => environment === 'production';
export const isStagingEnvironment = () => environment === 'staging';
export const isDevelopmentEnvironment = () => environment === 'development';

// URL helpers
export const getBaseUrl = (): string => config.app.siteUrl;
export const getDomain = (): string => config.app.domain;
export const getCanonicalUrl = (path: string = ''): string => {
  const baseUrl = getBaseUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

// Mercado Pago helpers
export const getMercadoPagoPublicKey = (): string => config.mercadoPago.publicKey;
export const getMercadoPagoAccessToken = (): string | undefined => config.mercadoPago.accessToken;

// Security helpers
export const getSecurityConfig = () => config.security;
export const getApiConfig = () => config.api;

// Validation
export const validateConfiguration = (): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!config.supabase.url) {
    errors.push('Supabase URL não configurada');
  }
  
  if (!config.supabase.anonKey) {
    errors.push('Supabase Anon Key não configurada');
  }
  
  if (!config.mercadoPago.publicKey || config.mercadoPago.publicKey.includes('YOUR_')) {
    errors.push('Chave pública do Mercado Pago não configurada corretamente');
  }
  
  if (isProductionEnvironment() && (!config.mercadoPago.accessToken || config.mercadoPago.accessToken.includes('YOUR_'))) {
    errors.push('Access Token do Mercado Pago não configurado para produção');
  }
  
  if (!config.app.domain) {
    errors.push('Domínio da aplicação não configurado');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Log current configuration (safe for production)
console.log(`🚀 PsyOnline iniciado em modo ${environment.toUpperCase()}`);
console.log(`🌐 Domínio: ${config.app.domain}`);
console.log(`🔗 URL: ${config.app.siteUrl}`);
console.log(`💳 Mercado Pago: ${config.mercadoPago.publicKey.substring(0, 20)}...`);

if (isDevelopmentEnvironment()) {
  console.log('🔧 Configuração completa (DEV):', config);
}

// Validate configuration on startup
const validation = validateConfiguration();
if (!validation.isValid) {
  console.error('❌ Erros de configuração encontrados:');
  validation.errors.forEach(error => console.error(`  - ${error}`));
  
  if (isProductionEnvironment()) {
    throw new Error('Configuração inválida para produção. Verifique as variáveis de ambiente.');
  }
}
